import { useEffect } from 'react';
import { logEvent } from '~/config/firebase.config';
import { ClientDatabase, Mode } from '~/types/client';
import { isLikelyMobile } from '~/utils/isLikelyMobile';
import { isLikelyPhone } from '~/utils/isLikelyPhone';

/**
 * Presence heartbeat for anim/bar screens, which (unlike provisioned airport
 * screens) have no instance-level heartbeat. Writes `screenInfos.anim.lastSeen`
 * on mount and every HEARTBEAT_MS, so the admin live dashboard can show the
 * screen as online for the whole session (menu and in-game alike).
 *
 * Anim-only by design (V1: one TV per establishment → the shared Mode.Anim key);
 * mounted at the screen-app root (ScreenApp) so it runs for the entire session.
 * Renders nothing. Never on the gameplay path.
 *
 * NOTE: HEARTBEAT_MS must stay comfortably below the dashboard's SELF_WINDOW_MS
 * (unboared-b2b useLiveSessions), or anim screens flicker offline between beats.
 */

const HEARTBEAT_MS = 60_000;

// `screen_on_phone` une fois par établissement et par chargement de page (le
// battement est remonté à chaque passage menu ↔ jeu).
const phoneLogged = new Set<string>();

export function ScreenHeartbeat({ clientID }: { clientID: string }) {
    useEffect(() => {
        // Page TV sur un téléphone : signalé à la télécommande (avertissement) et
        // compté une fois par chargement, avec l'appareil pour contrôler la détection.
        const onPhone = isLikelyPhone();
        // Tactile + petit écran mais pas « téléphone » : aussi tracé, pour vérifier
        // que la détection n'écarte pas de vrais téléphones (et quels appareils TV passent).
        const mobileNotPhone = !onPhone && isLikelyMobile();
        if ((onPhone || mobileNotPhone) && !phoneLogged.has(clientID)) {
            phoneLogged.add(clientID);
            logEvent(onPhone ? 'screen_on_phone' : 'screen_mobile_not_phone', clientID, {
                clientID,
                ua: navigator.userAgent.slice(0, 100),
                screen: `${window.screen.width}x${window.screen.height}`,
            });
        }
        // Présence = page VISIBLE : un onglet caché ne bat plus. Sur téléphone (onglet
        // gelé par iOS, appli quittée, page quittée), il retire aussi sa marque tout de
        // suite, sinon la télécommande le croirait « en direct » 4 min de plus (Le
        // Cuba, 20/10/2026). Les autres pages s'éteignent seules après 4 min, comme
        // avant (leur marque `tvSeen` est partagée avec la vraie TV).
        const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
        const beat = () => {
            if (isHidden()) return;
            ClientDatabase.setScreenLastSeen(clientID, Mode.Anim, onPhone).catch(() => {});
        };
        const onVisibility = () => {
            if (!isHidden()) beat();
            else if (onPhone) ClientDatabase.setScreenPhoneHidden(clientID, Mode.Anim).catch(() => {});
        };
        beat();
        const id = setInterval(beat, HEARTBEAT_MS);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisibility);
            // Téléphone qui quitte la page TV (« Retour », autre établissement) : plus
            // de TV ici. Passage menu ↔ jeu : le battement du montage suivant (même
            // appareil, écritures dans l'ordre) la rétablit aussitôt.
            if (onPhone) ClientDatabase.setScreenPhoneHidden(clientID, Mode.Anim).catch(() => {});
        };
    }, [clientID]); // changement d'établissement : on bat (et on compte) pour le nouveau

    return null;
}
